/**
 * Row-aware reordering for the Page-Settings "Containers" list.
 *
 * Works for ONE flat sibling list at any nesting level — top-level containers
 * on a canvas, or the nested children of a single parent. The list is a flat
 * 1-D list, but the canvas layout is 2-D (rows stacked vertically × up to
 * MAX_CONTAINERS_PER_ROW side-by-side per row, keyed by `rowIndex`/`order`).
 * `computeRowAwareReorder` translates a flat drag-reorder into the correct 2-D
 * layout intent:
 *
 *  - same-row : the drop keeps the container next to a current row-mate
 *               → reorder columns within that row (no rowIndex/width change).
 *  - join-row : dropped strictly BETWEEN two members of one OTHER row
 *               → join that row (column insert), if it has a free slot (<max)
 *               and no width-locked members.
 *  - row-move : every other cross-row drop (top, bottom, between different rows,
 *               next to a single-container row)
 *               → the container becomes its OWN full-width row at the drop
 *               position; rows below shift down. NO merging.
 *
 * Order within each row is intentionally NOT emitted here: the caller pairs
 * this with a REORDER_CONTAINERS command whose `newIds` already encode the
 * desired left-to-right order, so per-row `order` falls out correctly. This
 * helper only computes the `rowIndex`/`width` changes.
 *
 * Pure function — no React, no dispatch — so it is trivially unit-testable.
 */

import type { ContainerAPI } from '../../types/api.types';
import { CONTAINER_CONSTRAINTS } from '../constants/constraints';
import { calculateWidthsOnRemove, parseWidth } from './widthCalculations';

export type RowAwareReorderMode = 'noop' | 'same-row' | 'join-row' | 'row-move';

export interface ContainerReorderAssignment {
  containerId: string;
  /** New 0-based vertical row index. */
  rowIndex: number;
  /** New width (percentage string). Unchanged for shifted/untouched containers. */
  width: string;
}

export interface RowAwareReorderResult {
  mode: RowAwareReorderMode;
  /** Flat id order after the move — feed to REORDER_CONTAINERS. */
  newIds: string[];
  /** Containers whose rowIndex and/or width changed. Empty ⇒ ReorderContainers alone suffices. */
  assignments: ContainerReorderAssignment[];
}

const rowOf = (c: ContainerAPI): number => c.rowIndex ?? 0;
const widthOf = (c: ContainerAPI): string => c.configuration?.width ?? '100%';

/** Free width % remaining in a row after accounting for its current members. */
const freeWidthOfRow = (members: ContainerAPI[]): number =>
  100 - members.reduce((sum, c) => sum + parseWidth(widthOf(c)), 0);

/**
 * A moved container may JOIN a target row only if the row has a spare slot AND
 * enough free width to hold it at its current width — the moved container is
 * never resized and the row's existing members are never shrunk. A 1% tolerance
 * absorbs integer-rounding of stored widths. Otherwise the caller routes the
 * drop to its own new row instead.
 */
const joinFits = (
  siblings: ContainerAPI[],
  targetRow: number,
  movedId: string,
  moved: ContainerAPI,
  maxPerRow: number
): boolean => {
  const targetMembers = siblings.filter((c) => rowOf(c) === targetRow && c.id !== movedId);
  if (targetMembers.length >= maxPerRow) return false;
  return parseWidth(widthOf(moved)) <= freeWidthOfRow(targetMembers) + 1;
};

/**
 * Compute the layout changes for reordering a container within its flat sibling list.
 *
 * @param siblings        Sibling containers at one nesting level (top-level on a
 *                        canvas, or one parent's children), pre-sorted by
 *                        row-then-order (must match the order rendered in the list).
 * @param movedId         Id of the dragged container.
 * @param destinationIndex Drop index within the rendered list.
 * @param maxPerRow       Capacity per row (defaults to the shared constraint).
 */
export const computeRowAwareReorder = (
  siblings: ContainerAPI[],
  movedId: string,
  destinationIndex: number,
  maxPerRow: number = CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW
): RowAwareReorderResult => {
  const currentIds = siblings.map((c) => c.id);
  const sourceIndex = siblings.findIndex((c) => c.id === movedId);

  if (sourceIndex === -1) {
    return { mode: 'noop', newIds: currentIds, assignments: [] };
  }

  // Flat order after the move.
  const newIds = [...currentIds];
  newIds.splice(sourceIndex, 1);
  newIds.splice(destinationIndex, 0, movedId);

  const byId = new Map(siblings.map((c) => [c.id, c]));
  const moved = byId.get(movedId);
  if (!moved) {
    return { mode: 'noop', newIds: currentIds, assignments: [] };
  }
  const movedRow = rowOf(moved);

  const beforeId = destinationIndex - 1 >= 0 ? newIds[destinationIndex - 1] : undefined;
  const afterId = newIds[destinationIndex + 1];
  const before = beforeId ? byId.get(beforeId) : undefined;
  const after = afterId ? byId.get(afterId) : undefined;
  const beforeRow = before ? rowOf(before) : null;
  const afterRow = after ? rowOf(after) : null;

  // ── Decide the moved container's destination row ──
  // same-row: a bounding neighbour is already in moved's current row → column reorder.
  const staysInOwnRow =
    (before !== undefined && beforeRow === movedRow) ||
    (after !== undefined && afterRow === movedRow);

  // join: dropped strictly between two members of one OTHER row.
  const isJoinDrop =
    !staysInOwnRow &&
    before !== undefined &&
    after !== undefined &&
    beforeRow === afterRow &&
    beforeRow !== movedRow;

  let mode: RowAwareReorderMode;
  let targetRow = movedRow;
  let ownNewRow = false;

  if (staysInOwnRow) {
    mode = 'same-row';
    targetRow = movedRow;
  } else if (isJoinDrop && joinFits(siblings, beforeRow as number, movedId, moved, maxPerRow)) {
    // The moved container keeps its own width, so it may only JOIN a row with a
    // free slot AND enough free width to hold it as-is. A row that's already
    // full (by slots or width) can't take it → it becomes its own new row below,
    // never shrinking the row's existing members.
    mode = 'join-row';
    targetRow = beforeRow as number;
  } else {
    mode = 'row-move';
    ownNewRow = true;
  }

  // ── Build the final row layout (vertical list of id-arrays) ──
  // 1. Group NON-moved siblings by original row, preserving vertical+order sequence.
  const finalRows: Array<{ origRow: number; ids: string[] }> = [];
  for (const c of siblings) {
    if (c.id === movedId) continue;
    const r = rowOf(c);
    const last = finalRows[finalRows.length - 1];
    if (last && last.origRow === r) {
      last.ids.push(c.id);
    } else {
      finalRows.push({ origRow: r, ids: [c.id] });
    }
  }

  // 2. Place the moved container.
  if (ownNewRow) {
    // Insert as its own row immediately above the row containing `after`,
    // else append as the last row.
    let insertAt = finalRows.length;
    if (after !== undefined) {
      const idx = finalRows.findIndex((row) => row.ids.includes(after.id));
      if (idx !== -1) insertAt = idx;
    }
    finalRows.splice(insertAt, 0, { origRow: -1, ids: [movedId] });
  } else {
    // same-row or join: add into the target row, then sort that row by flat position.
    const idx = finalRows.findIndex((row) => row.origRow === targetRow);
    if (idx === -1) {
      finalRows.push({ origRow: -1, ids: [movedId] });
    } else {
      finalRows[idx].ids.push(movedId);
      finalRows[idx].ids.sort((a, b) => newIds.indexOf(a) - newIds.indexOf(b));
    }
  }

  // ── Width overrides ──
  // The moved container ALWAYS keeps its own width — on a join it fills part of
  // the target row's free space (existing members untouched), and on a row-move
  // it stands alone in its new row. Only the row it LEFT refills to 100%.
  const widthOverrides = new Map<string, string>();
  if (mode === 'join-row' || mode === 'row-move') {
    const sourceMembers = siblings.filter((c) => rowOf(c) === movedRow && c.id !== movedId);
    if (sourceMembers.length > 0) {
      calculateWidthsOnRemove([...sourceMembers, moved], movedId, 'equal').forEach((w, id) =>
        widthOverrides.set(id, w)
      );
    }
  }
  // same-row: widths never change.

  // ── Diff against current state → emit only rowIndex/width changes ──
  const assignments: ContainerReorderAssignment[] = [];
  finalRows.forEach((row, rowIdx) => {
    for (const id of row.ids) {
      const c = byId.get(id);
      if (!c) continue;
      const newWidth = widthOverrides.get(id) ?? widthOf(c);
      const rowChanged = rowOf(c) !== rowIdx;
      const widthChanged = widthOf(c) !== newWidth;
      if (!rowChanged && !widthChanged) continue;

      assignments.push({
        containerId: id,
        rowIndex: rowIdx,
        width: newWidth,
      });
    }
  });

  return { mode, newIds, assignments };
};

export default computeRowAwareReorder;
